import { useMemo, useRef, useState, type KeyboardEvent, type RefObject } from 'react';
import { CheckIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { OverlayScrollArea } from '@odo/shared-ui';
import { FloatingMenu } from '../FloatingMenu';

export interface SearchableSelectOption {
    value: string;
    label: string;
    sublabel?: string;
    /** Draws a divider above the row, e.g. to set an escape-hatch option apart. */
    separatedAbove?: boolean;
}

interface SearchableSelectMenuProps {
    triggerRef: RefObject<HTMLButtonElement>;
    listId: string;
    label: string;
    searchPlaceholder: string;
    options: readonly SearchableSelectOption[];
    multiple: boolean;
    isSelected: (value: string) => boolean;
    onPick: (value: string) => void;
    /** Multiple mode only: shown while something is selected. */
    onClear?: () => void;
    onClose: () => void;
}

const MENU_ESTIMATED_HEIGHT = 320;
const OPTION_SELECTOR = '[role="option"]';

function matches(option: SearchableSelectOption, term: string): boolean {
    const haystack = `${option.value} ${option.label} ${option.sublabel ?? ''}`.toLowerCase();
    return haystack.includes(term);
}

function SelectionMark({ multiple, checked }: { multiple: boolean; checked: boolean }) {
    if (multiple) {
        return (
            <input
                type="checkbox"
                readOnly
                checked={checked}
                tabIndex={-1}
                aria-hidden
                className="h-4 w-4 rounded border-gray-300 pointer-events-none accent-gray-900"
            />
        );
    }
    return (
        <CheckIcon
            aria-hidden
            className={`h-4 w-4 shrink-0 ${checked ? 'text-gray-900' : 'text-transparent'}`}
        />
    );
}

// Portalled so no scroll container or modal clips it. Escape is consumed here, so it closes only
// the menu: the modals around it listen on document, which the stopped event never reaches.
export function SearchableSelectMenu({
    triggerRef,
    listId,
    label,
    searchPlaceholder,
    options,
    multiple,
    isSelected,
    onPick,
    onClear,
    onClose
}: SearchableSelectMenuProps) {
    const [search, setSearch] = useState('');
    const listRef = useRef<HTMLDivElement>(null);
    const term = search.trim().toLowerCase();
    const visible = useMemo(
        () => (term ? options.filter((option) => matches(option, term)) : options),
        [options, term]
    );

    const moveFocus = (step: 1 | -1) => {
        const rows = Array.from(
            listRef.current?.querySelectorAll<HTMLElement>(OPTION_SELECTOR) ?? []
        );
        const current = rows.indexOf(document.activeElement as HTMLElement);
        const next = current === -1 ? (step === 1 ? 0 : rows.length - 1) : current + step;
        rows[Math.max(0, Math.min(rows.length - 1, next))]?.focus();
    };

    const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
            e.stopPropagation();
            onClose();
            triggerRef.current?.focus();
        } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            moveFocus(e.key === 'ArrowDown' ? 1 : -1);
        }
    };

    return (
        <FloatingMenu
            open
            triggerRef={triggerRef}
            onClose={onClose}
            matchTriggerWidth
            estimatedHeight={MENU_ESTIMATED_HEIGHT}
            className="min-w-[240px] rounded-xl border border-gray-200 bg-white shadow-[0_10px_28px_rgba(0,0,0,0.13)] overflow-hidden"
        >
            <div onKeyDown={onKeyDown} className="flex flex-col max-h-[inherit]">
                <div className="flex flex-shrink-0 items-center gap-2 px-3 h-10 border-b border-gray-100">
                    <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 shrink-0" />
                    <input
                        autoFocus
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={searchPlaceholder}
                        aria-label={searchPlaceholder}
                        aria-controls={listId}
                        className="flex-1 min-w-0 bg-transparent text-sm text-gray-700 placeholder-gray-400 outline-none"
                    />
                </div>
                {/* The shared overlay scrollbar; the fixed cap gives its region a height to scroll in. */}
                <OverlayScrollArea
                    className="flex flex-col min-h-0"
                    contentClassName="max-h-64"
                    trackInsetPx={4}
                >
                    <div
                        ref={listRef}
                        id={listId}
                        role="listbox"
                        aria-label={label}
                        aria-multiselectable={multiple || undefined}
                        className="py-1"
                    >
                        {visible.length === 0 && (
                            <div className="px-4 py-3 text-sm text-gray-400 text-center">
                                No matches
                            </div>
                        )}
                        {visible.map((option) => {
                            const checked = isSelected(option.value);
                            return (
                                <button
                                    key={option.value}
                                    type="button"
                                    role="option"
                                    aria-selected={checked}
                                    onClick={() => onPick(option.value)}
                                    className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none ${
                                        option.separatedAbove ? 'border-t border-gray-100' : ''
                                    }`}
                                >
                                    <SelectionMark multiple={multiple} checked={checked} />
                                    <span className="min-w-0 flex-1">
                                        <span className="block truncate text-sm text-gray-700">
                                            {option.label}
                                        </span>
                                        {option.sublabel && (
                                            <span className="block truncate text-[11px] text-gray-400">
                                                {option.sublabel}
                                            </span>
                                        )}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </OverlayScrollArea>
                {onClear && (
                    <div className="border-t border-gray-100 px-3 py-2">
                        <button
                            type="button"
                            onClick={onClear}
                            className="text-xs font-medium text-gray-500 hover:text-gray-800"
                        >
                            Clear selection
                        </button>
                    </div>
                )}
            </div>
        </FloatingMenu>
    );
}
